import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SITE from '../api';
import { useSite, IMG } from '../components/StoreLayout';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import { PORTAL, usePortal } from '../portalAuth';
import useSEO from '../useSEO';
import { STORE_BASE } from '../routeBase';

// Legal/utility pages under /store/page/:key with no admin-editable meta
// description of their own — mirrors PAGE_FALLBACK_DESCRIPTIONS in
// backend/routes/seo.js (that copy is what crawlers see; this one is what a
// browser sees after React mounts — kept in sync, same wording).
const PAGE_FALLBACK_DESCRIPTIONS = {
  'privacy-and-cookies': 'How Salvador Eyewear (Prakaash Eyewear Pvt. Ltd.) collects, uses and protects your data as a wholesale eyewear manufacturer and exporter.',
  'terms-and-conditions': 'Terms and conditions for ordering wholesale eyewear from Salvador Eyewear, a manufacturer and exporter based in Mumbai, India.',
  'disclaimer-policy': 'Disclaimer policy for Salvador Eyewear — product information, pricing and availability for wholesale optical frame buyers.',
  'delivery-information': 'Shipping and delivery information for wholesale eyewear orders from Salvador Eyewear, exporting to distributors and retailers worldwide.',
  'order-and-return': 'Order and return policy for wholesale eyewear purchases from Salvador Eyewear — how to place, track and return an order.',
  'manufacturers': 'The eyewear brands and manufacturers Salvador Eyewear supplies wholesale — acetate, TR90 and metal optical frames and sunglasses.',
  'brands': 'Browse the eyewear brands available wholesale from Salvador Eyewear — acetate, TR90 and metal optical frames and sunglasses for distributors and retailers.',
};

// ── BLOG LIST ────────────────────────────────────────────────────────────────
export function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { SITE.get('/blogs').then(r => setBlogs(r.data.blogs || [])).finally(() => setLoading(false)); }, []);

  useSEO({
    title: 'Blog',
    description: 'Eyewear industry insights, wholesale buying guides and product news from Salvador Eyewear — a wholesale eyeglass frame manufacturer and exporter.',
    keywords: 'eyewear blog, wholesale eyewear news, optical industry insights, eyeglass frame trends',
    canonical: `${window.location.origin}/store/blog`,
  });

  return (
    <div style={{ maxWidth: 1000, margin: '30px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 20 }}>Blog</h1>
      {loading ? <p style={{ color: '#aaa' }}>Loading…</p> : blogs.length === 0 ? (
        <p style={{ color: '#999' }}>No posts yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {blogs.map(b => (
            <Link key={b.slug} to={`${STORE_BASE}/blog/${b.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', height: '100%' }}>
                <div style={{ height: 160, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {b.cover_image ? <img src={IMG(b.cover_image)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <i className="ti ti-article" style={{ fontSize: 40, color: '#ddd' }} />}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, color: '#aaa', marginBottom: 6 }}>{b.published_at ? new Date(b.published_at).toLocaleDateString() : ''}</div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, lineHeight: 1.35 }}>{b.title}</h3>
                  <p style={{ fontSize: 13, color: '#777', lineHeight: 1.6 }}>{b.excerpt}</p>
                  <span style={{ fontSize: 13, color: 'var(--sf-accent)', fontWeight: 500 }}>Read more →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BLOG DETAIL ──────────────────────────────────────────────────────────────
export function BlogDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [more, setMore] = useState([]);
  const [nf, setNf] = useState(false);
  useEffect(() => {
    setB(null); setNf(false);
    SITE.get(`/blog/${slug}`).then(r => { setB(r.data.blog); setMore(r.data.more || []); }).catch(() => setNf(true));
  }, [slug]);

  useSEO({
    title: b?.meta_title || b?.title,
    description: b?.meta_description || b?.excerpt,
    keywords: b?.meta_keywords,
    image: b?.cover_image ? `${window.location.origin}/product-images/${b.cover_image}` : undefined,
    canonical: b ? `${window.location.origin}/store/blog/${b.slug}` : undefined,
  });

  if (nf) return <div style={{ padding: 60, textAlign: 'center' }}>Post not found. <Link to={`${STORE_BASE}/blog`} style={{ color: 'var(--sf-accent)' }}>Back to blog</Link></div>;
  if (!b) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 760, margin: '30px auto', padding: '0 24px' }}>
      <Link to={`${STORE_BASE}/blog`} style={{ fontSize: 13, color: 'var(--sf-accent)', textDecoration: 'none' }}>← Back to blog</Link>
      <h1 style={{ fontSize: 30, fontWeight: 700, margin: '14px 0 8px', lineHeight: 1.2 }}>{b.title}</h1>
      <div style={{ fontSize: 12.5, color: '#999', marginBottom: 20 }}>{b.author} · {b.published_at ? new Date(b.published_at).toLocaleDateString() : ''}</div>
      {b.cover_image && <img src={IMG(b.cover_image)} alt="" style={{ width: '100%', borderRadius: 12, marginBottom: 20 }} />}
      <div className="sf-richtext" style={{ fontSize: 15.5, lineHeight: 1.8, color: '#333' }} dangerouslySetInnerHTML={{ __html: b.body }} />
      <div style={{ marginTop: 28, padding: '14px 16px', background: '#f7f7f7', borderLeft: '3px solid var(--sf-accent)', borderRadius: '0 8px 8px 0', fontSize: 12, lineHeight: 1.7, color: '#777' }}>
        This article is provided for general informational purposes for opticians, retailers and eyewear buyers, and is not professional optometric or medical advice — for any eye-care concern, please consult a qualified optometrist or ophthalmologist.
        <br />© {new Date().getFullYear()} Prakaash Eyewear Pvt. Ltd. (Salvador Eyewear). Unauthorized copying, reproduction or republishing of this article, in whole or in part, without prior written permission is a violation of our{' '}
        <Link to={`${STORE_BASE}/page/terms-and-conditions`} style={{ color: 'var(--sf-primary)', fontWeight: 600 }}>Terms &amp; Conditions</Link> and may result in legal action.
      </div>
      {more.length > 0 && (
        <div style={{ marginTop: 40, borderTop: '1px solid #eee', paddingTop: 20 }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>More posts</h3>
          {more.map(m => <Link key={m.slug} to={`${STORE_BASE}/blog/${m.slug}`} style={{ display: 'block', padding: '8px 0', color: 'var(--sf-primary)', textDecoration: 'none', fontSize: 14 }}>→ {m.title}</Link>)}
        </div>
      )}
    </div>
  );
}

// Service-strip pages (the 3 homepage boxes between Men's Collection and Shop
// by Category) reuse their homepage banner image as the page hero, same as
// the About page does with about.image.
const PAGE_HERO_IMAGE = {
  'stock-rx-lenses': 'homepage.home_banner_5',
  'rx-swimming-goggles': 'homepage.home_banner_7',
  'pure-gold-frames': 'homepage.home_banner_8',
};

// ── GENERIC CONTENT PAGE (About + policies + other links) ────────────────────
export function ContentPage({ pageKey, titleKey, fallbackTitle }) {
  const { content, brands } = useSite();
  const params = useParams();
  const key = pageKey || params.page;
  const [body, setBody] = useState(null);
  const [nf, setNf] = useState(false);

  useEffect(() => {
    // About page uses about.title/about.body from content; the certification
    // page is its own gated component, not content-editor HTML; others use
    // page.<key>
    if (key === 'about' || key === 'certifications') { setBody(content['about.body'] || ''); return; }
    SITE.get(`/page/${key}`).then(r => setBody(r.data.body)).catch(() => setNf(true));
  }, [key, content]);

  const title = titleKey ? content[titleKey]
    : content[`page.${key}.title`] || fallbackTitle || key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const showBrandStrip = (key === 'about' || key === 'brands') && brands?.length > 0;
  const heroImage = key === 'about' ? content['about.image'] : content[PAGE_HERO_IMAGE[key]];

  const metaPrefix = key === 'about' ? 'about' : `page.${key}`;
  useSEO({
    title: content[`${metaPrefix}.meta_title`] || title,
    description: content[`${metaPrefix}.meta_description`] || PAGE_FALLBACK_DESCRIPTIONS[key],
    keywords: content[`${metaPrefix}.meta_keywords`],
    canonical: `${window.location.origin}/store/page/${key}`,
  });

  if (key === 'certifications') return <CertificationsGate />;
  if (nf) return <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Page not found.</div>;
  return (
    <div style={{ maxWidth: 800, margin: '30px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 18, textTransform: 'capitalize' }}>{title}</h1>
      {heroImage && (
        <img src={IMG(heroImage)} alt="" style={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 12, marginBottom: 20 }} />
      )}
      <div className="sf-richtext" style={{ fontSize: 15, lineHeight: 1.8, color: '#444' }} dangerouslySetInnerHTML={{ __html: body || '' }} />
      {showBrandStrip && <BrandStrip brands={brands} />}
    </div>
  );
}

const CERT_STATUS = {
  valid:         { label: 'Valid',         color: '#16a34a' },
  expiring_soon: { label: 'Expiring Soon', color: '#d97706' },
  expired:       { label: 'Expired',       color: '#dc2626' },
  unknown:       { label: '',              color: '#999' },
};
const isPdfFile = p => /\.pdf$/i.test(p || '');

// ── COMPANY & PRODUCT CERTIFICATION — gated behind customer login AND a
// separate certificate_access flag the admin grants per customer (Customers
// > Permissions). Three states: not logged in, logged in but not granted,
// granted (shows the certificates).
function CertificationsGate() {
  const { customer, ready } = usePortal();
  const [state, setState] = useState({ loading: true, certs: null, deniedMessage: '' });

  useEffect(() => {
    if (!ready) return;
    if (!customer) { setState({ loading: false, certs: null, deniedMessage: '' }); return; }
    PORTAL.get('/certifications')
      .then(r => setState({ loading: false, certs: r.data.certifications || [], deniedMessage: '' }))
      .catch(e => setState({ loading: false, certs: null, deniedMessage: e.response?.data?.message || 'Access denied.' }));
  }, [ready, customer]);

  if (!ready || state.loading) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;

  if (!customer) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 24px', textAlign: 'center' }}>
        <i className="ti ti-lock" style={{ fontSize: 40, color: '#ccc' }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '14px 0 10px' }}>Company & Product Certification</h1>
        <p style={{ color: '#666', fontSize: 14.5, lineHeight: 1.7, marginBottom: 22 }}>
          Our CE, ISO 9001:2015 and MSME Udyam certification documents are visible only to registered customers we've given access to.
          Please register or log in — if your account doesn't have access yet, contact us to request it.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Link to={`${STORE_BASE}/login`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '9px 22px', borderRadius: 22, fontSize: 13.5, fontWeight: 600, textDecoration: 'none' }}>Login</Link>
          <Link to={`${STORE_BASE}/register`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '9px 22px', borderRadius: 22, fontSize: 13.5, fontWeight: 600, textDecoration: 'none' }}>Register</Link>
        </div>
      </div>
    );
  }

  if (state.deniedMessage) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 24px', textAlign: 'center' }}>
        <i className="ti ti-lock" style={{ fontSize: 40, color: '#ccc' }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '14px 0 10px' }}>Company & Product Certification</h1>
        <p style={{ color: '#666', fontSize: 14.5, lineHeight: 1.7 }}>{state.deniedMessage}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '30px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6 }}>Company & Product Certification</h1>
      <p style={{ color: '#888', fontSize: 13.5, marginBottom: 24 }}>Genuine certification documents for Prakaash Eyewear Pvt. Ltd.</p>
      {(!state.certs || state.certs.length === 0) ? (
        <p style={{ color: '#999' }}>No certificates published yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {state.certs.map(c => {
            const st = CERT_STATUS[c.status] || CERT_STATUS.unknown;
            return (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ aspectRatio: '7/4', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {c.header_image
                    ? <img src={IMG(c.header_image)} alt={c.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <i className="ti ti-certificate" style={{ fontSize: 36, color: '#ddd' }} />}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{c.name}</h3>
                    {st.label && <span style={{ fontSize: 11, fontWeight: 700, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', lineHeight: 1.8, marginTop: 8 }}>
                    {c.cert_number && <div>Certificate No: {c.cert_number}</div>}
                    {c.issuing_body && <div>Issued by: {c.issuing_body}</div>}
                    {c.issue_date && <div>Issue Date: {c.issue_date}</div>}
                    {c.expiry_date && <div>Valid Until: {c.expiry_date}</div>}
                  </div>
                  {c.scan_file && (
                    <a href={IMG(c.scan_file)} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-block', marginTop: 12, fontSize: 13, fontWeight: 600, color: 'var(--sf-accent)', textDecoration: 'none' }}>
                      View scanned copy {isPdfFile(c.scan_file) ? '(PDF)' : ''} →
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── BRANDS PAGE — sequential logo + description blocks (matches the old
// site's layout: alternating logo/text position, "Shop {brand}" link,
// divider between brands) rather than a plain logo grid ────────────────────
export function Brands() {
  const { content: c, brands } = useSite();
  useSEO({
    title: 'Our Brands',
    description: c['page.brands.meta_description'] || PAGE_FALLBACK_DESCRIPTIONS.brands,
    keywords: c['page.brands.meta_keywords'],
    canonical: `${window.location.origin}/store/page/brands`,
  });
  return (
    <div style={{ maxWidth: 900, margin: '30px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 18 }}>Brands</h1>
      {c['page.brands'] && (
        <div className="sf-richtext" style={{ fontSize: 15, lineHeight: 1.8, color: '#444', marginBottom: 30 }} dangerouslySetInnerHTML={{ __html: c['page.brands'] }} />
      )}
      {(!brands || brands.length === 0) ? (
        <p style={{ color: '#999' }}>No brands yet.</p>
      ) : (
        brands.map((b, i) => (
          <div key={b.id} style={{
            display: 'flex', flexWrap: 'wrap', gap: 30, alignItems: 'center', padding: '30px 0',
            borderBottom: i < brands.length - 1 ? '1px solid #eee' : 'none',
            flexDirection: i % 2 === 1 ? 'row-reverse' : 'row',
          }}>
            <div style={{ flex: '0 0 220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {b.logo
                ? <img src={IMG(b.logo)} alt={b.name} loading="lazy" style={{ maxWidth: '100%', maxHeight: 90, objectFit: 'contain' }} />
                : <div style={{ fontSize: 16, fontWeight: 700, color: '#191919', textAlign: 'center' }}>{b.name}</div>}
            </div>
            <div style={{ flex: '1 1 320px' }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>{b.name}</h3>
              {b.description
                ? <p style={{ fontSize: 14.5, lineHeight: 1.75, color: '#555', marginBottom: 12 }}>{b.description}</p>
                : <p style={{ fontSize: 14.5, color: '#aaa', fontStyle: 'italic', marginBottom: 12 }}>More about {b.name} coming soon.</p>}
              <Link to={`${STORE_BASE}/shop?brand=${encodeURIComponent(b.name)}`} style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sf-accent)', textDecoration: 'none' }}>Shop {b.name} →</Link>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Row of active brand logos, reused on the About page and the Brands page.
function BrandStrip({ brands }) {
  return (
    <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid #eee' }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#666', marginBottom: 16 }}>Our Brands</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
        {brands.map(b => (
          <Link key={b.id} to={`${STORE_BASE}/shop?brand=${encodeURIComponent(b.name)}`} title={`Shop ${b.name}`}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 100, textDecoration: 'none' }}>
            <div className="sf-brand-tile" style={{ width: 90, height: 60, border: '1px solid #eee', borderRadius: 8, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {b.logo
                ? <img src={IMG(b.logo)} alt={b.name} loading="lazy" style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain' }} />
                : <span style={{ fontSize: 10, color: '#bbb', textAlign: 'center', padding: 4 }}>{b.name}</span>}
            </div>
            <span style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>{b.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── CONTACT PAGE ─────────────────────────────────────────────────────────────
export function Contact() {
  const { content: c } = useSite();
  const [f, setF] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const set = k => e => setF({ ...f, [k]: e.target.value });

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!f.message) return setErr('Please enter a message');
    setBusy(true);
    try { await SITE.post('/enquiry', { ...f, source: 'contact', captcha_token: captchaToken }); setSent(true); }
    catch (e2) { setErr(e2.response?.data?.error || 'Could not send'); }
    setBusy(false);
  };

  return (
    <div style={{ maxWidth: 1000, margin: '30px auto', padding: '0 24px', display: 'flex', gap: 30, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 300px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 18 }}>{c['contact.title'] || 'Contact Us'}</h1>
        {c['contact.image'] && (
          <img src={IMG(c['contact.image'])} alt="" style={{ width: '100%', maxWidth: 380, borderRadius: 12, marginBottom: 18, display: 'block' }} />
        )}
        {c['contact.intro'] && <p style={{ fontSize: 14.5, color: '#444', lineHeight: 1.7, marginBottom: 16 }}>{c['contact.intro']}</p>}
        <div style={{ fontSize: 14, lineHeight: 2, color: '#444' }}>
          <div><i className="ti ti-map-pin" style={{ color: 'var(--sf-accent)' }} /> {c['brand.address']}</div>
          <div><i className="ti ti-phone" style={{ color: 'var(--sf-accent)' }} /> {c['brand.phone']}</div>
          <div><i className="ti ti-mail" style={{ color: 'var(--sf-accent)' }} /> {c['brand.email']}</div>
          <div><i className="ti ti-clock" style={{ color: 'var(--sf-accent)' }} /> {c['brand.hours']}</div>
        </div>
      </div>
      <div style={{ flex: '1 1 340px' }}>
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 24 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <i className="ti ti-circle-check" style={{ fontSize: 44, color: '#16a34a' }} />
              <h3 style={{ marginTop: 10 }}>Thank you!</h3>
              <p style={{ color: '#666', fontSize: 14 }}>Your enquiry has been sent. We will contact you soon.</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 14 }}>Send an enquiry</h3>
              {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '8px 11px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
              <I label="Name" value={f.name} onChange={set('name')} />
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}><I label="Email" value={f.email} onChange={set('email')} /></div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Phone</label>
                  <StorePhoneInput value={f.phone} onChange={v => setF(prev => ({ ...prev, phone: v }))} />
                </div>
              </div>
              <I label="Subject" value={f.subject} onChange={set('subject')} />
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Message</label>
                <textarea value={f.message} onChange={set('message')} rows={4} style={{ ...inp, resize: 'vertical' }} />
              </div>
              <Captcha onToken={setCaptchaToken} />
              <button type="submit" disabled={busy} className="sf-btn-outline" style={{ width: '100%', padding: 11, border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {busy ? 'Sending…' : 'Send enquiry'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
function I({ label, value, onChange }) {
  return <div style={{ marginBottom: 12 }}><label style={lbl}>{label}</label><input value={value} onChange={onChange} style={inp} /></div>;
}
